import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Select from 'react-select';

import ClientCard from '@/components/ClientsPage/ClientList/ClientCard';
import classes from './style.module.scss';
import CardList from '@/components/common/CardList';
import Spinner from '@/components/common/Spinner';
import { useClients } from '@/hooks/useClients';
import { setOptions, setQuery } from '@/store/ducks/filter';

const ClientList = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const query = useSelector(state => state.filter.query);
  const cardControl = useSelector(state => state.cards);

  const [orderByDir, setOrderByDir] = useState('name_asc');
  const [filterBillingModality, setFilterBillingModality] = useState('ALL');

  const status = params.status || 'enabled';
  const { lastReload } = cardControl;

  const billingModalityOptions = [
    { value: 'ALL', label: intl.formatMessage({ id: 'clientList.billingModality.ALL' }) },
    { value: 'SEPARATE', label: intl.formatMessage({ id: 'clientList.billingModality.SEPARATE' }) },
    { value: 'PRE_PAID', label: intl.formatMessage({ id: 'clientList.billingModality.PRE_PAID' }) },
    { value: 'POST_PAID', label: intl.formatMessage({ id: 'clientList.billingModality.POST_PAID' }) },
  ];

  const orderOptions = [
    { value: 'name_asc', label: intl.formatMessage({ id: 'clientList.order.name_asc' }) },
    { value: 'balance_asc', label: intl.formatMessage({ id: 'clientList.order.balance_asc' }) },
    { value: 'balance_desc', label: intl.formatMessage({ id: 'clientList.order.balance_desc' }) },
    { value: 'last_order_at_desc', label: intl.formatMessage({ id: 'clientList.order.last_order_at_desc' }) },
  ];

  const scrollParentRef = useRef(null);

  const { useInfiniteClientsList } = useClients();

  const order_by = orderByDir.replace(/_(asc|desc)$/, '');
  const order_by_dir = orderByDir.endsWith('_desc') ? 'desc' : 'asc';

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, refetch } = useInfiniteClientsList({
    text: query.text,
    order_by,
    order_by_dir,
    status,
    billing_modality: filterBillingModality !== 'ALL' ? [filterBillingModality] : null,
  });

  const prevQueryRef = useRef(query);

  useEffect(() => {
    dispatch(setOptions({ basic: ['text'] }));
    if (!query.text) dispatch(setQuery({}));
  }, [dispatch]);

  useEffect(() => {
    refetch();
  }, [status, orderByDir, filterBillingModality, lastReload, refetch]);

  useEffect(() => {
    if (JSON.stringify(prevQueryRef.current) !== JSON.stringify(query)) {
      prevQueryRef.current = query;
      if (Object.entries(query).length > 0 && location.pathname !== `/clients/${status}`) {
        navigate(`/clients/${status}`);
      }
    }
  }, [location.pathname, navigate, query, status]);

  const cards = useMemo(() => {
    return data?.pages.flatMap(page => page.map(item => ({ data: item }))) || [];
  }, [data]);

  return (
    <div className={classes.wrapper}>
      <div className={classes.selectContainer}>
        <Select
          value={billingModalityOptions?.find(option => option.value === filterBillingModality || '')}
          options={billingModalityOptions}
          onChange={e => setFilterBillingModality(e.value)}
          classNamePrefix="customSelectBillingModality"
          className={classes.customSelect}
          placeholder={intl.formatMessage({ id: 'BILLING_MODALITIES' })}
        />
        <Select
          value={orderOptions?.find(option => option.value === orderByDir || '')}
          options={orderOptions}
          onChange={e => setOrderByDir(e.value)}
          classNamePrefix="customSelect"
          className={classes.customSelect}
        />
      </div>
      <div className={classes.cardListContainer}>
        {isLoading ? (
          <div className={classes.spinner}>
            <Spinner />
          </div>
        ) : (
          <CardList
            cards={cards || []}
            Element={ClientCard}
            hasMore={hasNextPage}
            loadMore={() => {
              if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
              }
            }}
            scrollParentRef={scrollParentRef}
          />
        )}
      </div>
    </div>
  );
};

export default ClientList;
